import React from 'react';
import { motion } from 'framer-motion';
import type { NewDiscovery } from '../types';
import { iconFor } from '../lib/utils';
import { CONSTANTS } from '../data.js';

type NewDiscoveryPopupProps = {
  discovery: NewDiscovery;
  onClose: () => void;
};

export const NewDiscoveryPopup: React.FC<NewDiscoveryPopupProps> = ({ discovery, onClose }) => {
  return (
    <motion.div
      className="absolute inset-0 flex items-center justify-center z-50"
      style={{ background: 'rgba(26, 22, 17, 0.85)' }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <motion.div
        className="relative flex flex-col items-center p-8 rounded-xl max-w-[380px]"
        style={{
          background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.surface} 0%, ${CONSTANTS.ui.theme.colors.background} 100%)`,
          border: `2px solid ${CONSTANTS.ui.theme.colors.primary}`,
          boxShadow: `${CONSTANTS.ui.theme.effects.ornateBoxShadow}, 0 0 20px rgba(218, 165, 32, 0.3)`
        }}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        transition={{ duration: 0.2 }}
      >
        {/* Decorative corners */}
        <div className="absolute top-3 left-3 w-6 h-6 border-l-2 border-t-2 rounded-tl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute top-3 right-3 w-6 h-6 border-r-2 border-t-2 rounded-tr-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute bottom-3 left-3 w-6 h-6 border-l-2 border-b-2 rounded-bl-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />
        <div className="absolute bottom-3 right-3 w-6 h-6 border-r-2 border-b-2 rounded-br-lg" style={{ borderColor: CONSTANTS.ui.theme.colors.primary }} />

        <motion.div 
          className="font-cinzel text-2xl font-bold mb-4"
          style={{ 
            color: CONSTANTS.ui.theme.colors.primary,
            textShadow: CONSTANTS.ui.theme.effects.textGlow
          }}
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          New Discovery!
        </motion.div>
        
        <motion.div 
          className="rounded-xl mb-4 flex items-center justify-center overflow-hidden"
          style={{ 
            width: 120, 
            height: 120,
            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary}20 0%, ${CONSTANTS.ui.theme.colors.accent}20 100%)`,
            border: `2px solid ${CONSTANTS.ui.theme.colors.primary}`,
            boxShadow: `inset 0 2px 4px rgba(0, 0, 0, 0.2), 0 4px 8px rgba(218, 165, 32, 0.2)`
          }}
          initial={{ scale: 0, rotate: -180 }}
          animate={{ scale: 1, rotate: 0 }}
          transition={{ delay: 0.2, duration: 0.5, type: "spring" }}
        >
          <img
            src={iconFor(discovery.key)}
            alt={discovery.label}
            className="object-contain max-w-full max-h-full p-2"
          />
        </motion.div>
        
        <motion.div 
          className="font-cinzel text-2xl font-semibold text-center mb-3"
          style={{ color: CONSTANTS.ui.theme.colors.text }}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.3 }}
        >
          {discovery.label}
        </motion.div>
        
        <motion.div 
          className="font-crimson text-sm text-center mb-6 leading-relaxed"
          style={{ color: CONSTANTS.ui.theme.colors.textSecondary }}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          {discovery.description}
        </motion.div>
        
        <motion.button
          onClick={onClose}
          className="font-crimson px-8 py-3 rounded-lg text-lg font-semibold transition-all duration-200 hover:scale-105 active:scale-95"
          style={{
            background: `linear-gradient(135deg, ${CONSTANTS.ui.theme.colors.primary} 0%, ${CONSTANTS.ui.theme.colors.accent} 100%)`,
            color: CONSTANTS.ui.theme.colors.background,
            border: `1px solid ${CONSTANTS.ui.theme.colors.border}`,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)'
          }}
          initial={{ y: 10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.5 }}
        >
          Continue Quest
        </motion.button>
      </motion.div>
    </motion.div>
  );
};
